import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  awakenPet,
  feedPet,
  getPetScreen,
  renamePet,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { CardElement } from "@/types/battle"
import { AFFILIATION_ELEMENTS } from "@/constants/affiliationInfo"
import { ELEMENT_NAMES } from "@/constants/guardians"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function PetPage() {
  const queryClient = useQueryClient()
  const { syncGameState } = useGuardiansPassport()
  const [name, setName] = useState("")
  const [element, setElement] = useState<CardElement>("FIRE")

  const q = useQuery({
    queryKey: ["pet-screen"],
    queryFn: getPetScreen,
  })

  const pet = q.data?.pet

  const invalidate = async () => {
    await syncGameState()
    void queryClient.invalidateQueries({ queryKey: ["pet-screen"] })
  }

  const awaken = useMutation({
    mutationFn: () => awakenPet({ element, displayName: name || undefined }),
    onSuccess: invalidate,
  })

  const feed = useMutation({
    mutationFn: () => feedPet(1),
    onSuccess: invalidate,
  })

  const rename = useMutation({
    mutationFn: () => renamePet(name),
    onSuccess: invalidate,
  })

  return (
    <GameScreenLayout title="Питомец" backTo="/play/profile">
      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : pet ? (
        <div className="space-y-3 text-sm">
          <p className="text-lg font-medium">{pet.displayName ?? "Питомец"}</p>
          <p className="text-guardians-muted">
            {ELEMENT_NAMES[pet.element]} · ур. {pet.level} · XP {pet.xp}/{pet.maxXp}
          </p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Имя"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => feed.mutate()}
              className="flex-1 rounded-xl bg-guardians-accent py-2 text-guardians-bg"
            >
              Покормить
            </button>
            <button
              type="button"
              disabled={!name.trim()}
              onClick={() => rename.mutate()}
              className="flex-1 rounded-xl border border-white/20 py-2"
            >
              Переименовать
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-guardians-muted">Пробудите питомца</p>
          <select
            value={element}
            onChange={(e) => setElement(e.target.value as CardElement)}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
          >
            {AFFILIATION_ELEMENTS.map((el) => (
              <option key={el} value={el}>
                {ELEMENT_NAMES[el]}
              </option>
            ))}
          </select>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Имя (необязательно)"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => awaken.mutate()}
            className="w-full rounded-xl bg-guardians-accent py-3 text-guardians-bg"
          >
            Пробудить
          </button>
        </div>
      )}

      {(awaken.isError || feed.isError || rename.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(
            awaken.error ?? feed.error ?? rename.error,
            "Ошибка питомца"
          )}
        </p>
      )}
    </GameScreenLayout>
  )
}
